import { cn } from "../../lib/cn";

/**
 * Phase 2 primitive — `<Pipeline>` renders a horizontal stages strip
 * matching `.aud-pipeline`, `.ld-pipeline`, and the timeline strip used
 * on AuditsPage / LeadsPage. Each stage shows a label, count badge, and
 * a fill bar whose width reflects progress. The active stage gets an
 * accent colour.
 *
 * Stages are rendered in order. The caller decides which is active via
 * `activeIndex`. Click handlers are optional; Pipeline does not enforce
 * navigation semantics.
 *
 * Shape:
 *   stages = [{ key, label, count?, fillPct? }, ...]
 */
export default function Pipeline({ stages, activeIndex, onStageClick, className }) {
  const items = Array.isArray(stages) ? stages : [];
  return (
    <div className={cn("flex items-center gap-2 flex-wrap", className)}>
      {items.map((stage, i) => {
        const active = i === activeIndex;
        const clickable = typeof onStageClick === "function";
        return (
          <div
            key={stage.key ?? i}
            onClick={clickable ? () => onStageClick(stage, i) : undefined}
            className={cn(
              "flex-1 min-w-[110px] rounded-lg border px-3 py-2.5 transition-colors",
              active
                ? "border-rc-accent bg-[rgba(59,127,250,0.12)]"
                : "border-rc-border bg-rc-surface",
              clickable && "cursor-pointer hover:border-rc-blue-400",
            )}
          >
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <div className={cn(
                "text-[10.5px] font-bold uppercase tracking-[0.09em]",
                active ? "text-rc-accent-strong" : "text-rc-text-secondary",
              )}>
                {stage.label}
              </div>
              {typeof stage.count === "number" && (
                <span className={cn(
                  "font-mono text-[10px] px-1.5 py-px rounded-[10px]",
                  active
                    ? "bg-[rgba(59,127,250,0.2)] text-rc-accent-strong"
                    : "bg-rc-border text-rc-text-secondary",
                )}>
                  {stage.count}
                </span>
              )}
            </div>
            {typeof stage.fillPct === "number" && (
              <div className="h-1 rounded bg-rc-border overflow-hidden">
                <div
                  className={cn(
                    "h-full rounded transition-[width] duration-300",
                    active ? "bg-rc-accent" : "bg-rc-blue-400",
                  )}
                  style={{ width: `${Math.max(0, Math.min(100, stage.fillPct))}%` }}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
